/**
 * Dividend Quality Score (0–100) — June 2026 overhaul.
 *
 * New 100-point model = 60 Trailing Core + 40 Forward/Dynamic Signals:
 *   • Trailing Core (0-60) — the legacy pillar math (Safety / Yield /
 *     Growth / Longevity / Fundamentals, 0-100) compressed ×0.6.
 *   • Forward Signals (0-40) — SEC XBRL distribution coverage:
 *       – Coverage ratio (0-25, sliding scale, rolling-TTM smoothed)
 *         with asset-class denominators: FCF corporates / FFO REITs /
 *         NII BDCs+CEFs (see backend services/sec_coverage.py)
 *       – Coverage trend (0-15, TTM drift over ~4 quarters)
 *   • No coverage data yet → PARTIAL score (max 60) with a note; the
 *     backend fetcher upgrades it automatically when SEC data lands.
 *
 * Returns null when we don't have enough data to score honestly.
 * MUST stay aligned with `backend/server.py::compute_quality_score`.
 */

const REIT_SECTORS = new Set(['Real Estate', 'Real Estate Investment Trust']);

/**
 * Forward/Dynamic signals (0-40) from the stock's `sec_coverage`
 * payload. Returns null when unavailable — callers emit partial score.
 * Mirrors backend `_coverage_forward_points`.
 */
function coverageForward(stock) {
  const cov = stock?.sec_coverage;
  if (!cov || cov.status !== 'ok') return null;
  const r = Number(cov.ratio_current);
  if (!Number.isFinite(r)) return null;

  let covPts;
  if (r >= 2.5) covPts = 25;
  else if (r >= 1.0) covPts = 10 + (r - 1.0) * 10;
  else if (r >= 0.5) covPts = (r - 0.5) * 20;
  else covPts = 0;

  const t = Number(cov.trend_pct);
  let trendPts;
  if (!Number.isFinite(t)) trendPts = 7;
  else if (t >= 15) trendPts = 15;
  else if (t >= -15) trendPts = 5 + ((t + 15) / 30) * 10;
  else if (t >= -40) trendPts = ((t + 40) / 25) * 5;
  else trendPts = 0;

  return {
    total: covPts + trendPts,
    coveragePts: Math.round(covPts * 10) / 10,
    trendPts: Math.round(trendPts * 10) / 10,
    ratio: r,
    trendPct: Number.isFinite(t) ? t : null,
    metric: cov.metric || 'FCF',
    latestQuarterEnd: cov.latest_quarter_end || null,
    quarters: cov.quarters || [],
    mode: cov.mode || null,
  };
}

/**
 * Compute the 0–100 quality score for a single stock-cache record.
 * Returns `null` if the stock has zero income data.
 */
export function computeQuality(stock) {
  if (!stock) return null;

  // Round 97d — Non-dividend short-circuit (unchanged). Growth stocks
  // carry the persisted Non-Dividend Quality Score instead.
  if (stock.pays_dividend === false) {
    const ndScore = Number(stock.non_dividend_quality_score);
    if (!Number.isFinite(ndScore)) return null;
    const tier = (stock.non_dividend_quality_tier || '').toUpperCase();
    const points = (stock.non_dividend_quality_signals || {}).points || null;
    return {
      score: Math.round(ndScore),
      breakdown: {},
      penalty: 0,
      is_non_dividend: true,
      non_dividend_tier: tier || null,
      non_div_points: points,
    };
  }

  const yieldPct = Number(stock.dividend_yield);
  const payoutRaw = Number(stock.payout_ratio);
  let growthPct = Number(stock.cagr_5yr);
  if (!Number.isFinite(growthPct)) {
    const w = Number(stock.dividend_cagr_5y);
    if (Number.isFinite(w)) growthPct = w;
    else growthPct = NaN;
  }
  const tier = (stock.dividend_tier || '').toLowerCase();
  const streakYears = Number(stock.years_paying || stock.consecutive_years || 0);
  const isReit = REIT_SECTORS.has(stock.sector);
  const isLeveraged = !!stock.is_leveraged;
  const isCef = !!stock.is_cef;

  let isEtf = !!stock.is_etf;
  if (!isEtf) {
    const name = (stock.name || '').toUpperCase();
    const sector = (stock.sector || '').trim();
    if (!sector && (
      ` ${name} `.includes(' ETF ')
      || name.endsWith(' ETF')
      || ` ${name} `.includes(' FUND ')
      || ` ${name} `.includes(' TRUST ')
    )) {
      isEtf = true;
    }
  }
  const isMutualFund = !!stock.is_mutual_fund;
  const isFund = isEtf || isMutualFund || isCef;

  const hasYield = Number.isFinite(yieldPct) && yieldPct > 0;
  const hasPayout = Number.isFinite(payoutRaw) && payoutRaw > 0;
  const hasGrowth = Number.isFinite(growthPct);

  const forward = coverageForward(stock);

  // ─── FUND PATHWAY ─────────────────────────────────────────────
  if (isFund) {
    if (!hasYield && !tier) return null;
    const breakdown = { yield: 0, growth: 0, longevity: 0, confidence: 0 };

    const avg3 = Number(stock.avg_yield_3yr || 0);
    if (hasYield) {
      if (avg3 > 0) {
        const variance = Math.abs(yieldPct - avg3) / Math.max(avg3, 0.01);
        if (variance <= 0.10) breakdown.yield = 30;
        else if (variance <= 0.25) breakdown.yield = 22;
        else if (variance <= 0.50) breakdown.yield = 12;
        else breakdown.yield = 5;
      } else {
        breakdown.yield = 15;
      }
    }

    if (hasGrowth) {
      // Round P132b (Feb 2026) — Tightened growth bands so
      // CEF-inflated CAGRs (30-50%+ driven by return-of-capital or
      // special distributions) don't score full marks. Realistic
      // dividend growth caps around 15-20%/yr even for the fastest
      // legitimate growers (early NVDA, VICI). Anything above 25%
      // is almost certainly not organic growth — score reflects
      // that skepticism.
      // Oct 2026 — a "5-year" growth rate needs a track record: funds
      // younger than 3 years (EGGY, XLEI) get a flat partial 8, never 25.
      // `years_paying` for funds is stamped server-side from ex-div history.
      if (streakYears < 3) breakdown.growth = 8;                             // too new to grade
      else if (growthPct >= 0 && growthPct <= 20) breakdown.growth = 25;    // sweet spot
      else if (growthPct > 20 && growthPct <= 25) breakdown.growth = 20;    // borderline
      else if (growthPct > 25 && growthPct <= 50) breakdown.growth = 12;    // suspicious — likely inflated
      else if (growthPct > 50) breakdown.growth = 8;                        // almost certainly RoC / specials
      else if (growthPct > -5 && growthPct < 0) breakdown.growth = 12;
      else breakdown.growth = 0;
    }

    if (tier === 'king') breakdown.longevity = 25;
    else if (tier === 'aristocrat') breakdown.longevity = 20;
    else if (tier === 'achiever') breakdown.longevity = 15;
    else if (streakYears >= 10) breakdown.longevity = 12;
    else if (streakYears >= 5) breakdown.longevity = 8;
    else if (streakYears >= 2) breakdown.longevity = 4;

    if (stock.quote_type && stock.fund_category) breakdown.confidence = 20;
    else if (stock.quote_type) breakdown.confidence = 15;
    else breakdown.confidence = 5;

    let penalty = 0;
    const penaltyReasons = [];
    if (isLeveraged) { penalty += 15; penaltyReasons.push({ key: 'leveraged', label: 'Leveraged fund', delta: -15 }); }
    if (isCef)       { penalty += 5;  penaltyReasons.push({ key: 'cef',       label: 'CEF structure', delta: -5 }); }

    const raw = breakdown.yield + breakdown.growth + breakdown.longevity + breakdown.confidence - penalty;
    const fundScore = Math.max(0, Math.min(90, Math.round(raw)));
    // CEF/BDC-flavoured funds with SEC coverage data get the 60/40
    // treatment; plain ETFs/MFs (no 10-Q filings) keep the fund score.
    const score = forward
      ? Math.max(0, Math.min(90, Math.round(fundScore * 0.6 + forward.total)))
      : fundScore;
    return {
      score,
      breakdown,
      penalty,
      penaltyReasons,
      fundamentalReasons: [],
      isReit,
      isLeveraged,
      isCef,
      isEtf,
      isFund: true,
      fundCategory: stock.fund_category || null,
      forward,
      trailingCore: forward ? Math.round(fundScore * 0.6) : null,
      partial: false,
    };
  }

  // ─── CORPORATE STOCK PATHWAY ───────────────────────────────────
  if (!hasYield && !hasPayout && !tier) return null;

  const revGrowth1y = Number(stock.revenue_growth_1y ?? stock.revenue_growth);
  const trapSignature = Number.isFinite(revGrowth1y) && revGrowth1y <= -0.25;

  const breakdown = { safety: 0, yield: 0, growth: 0, longevity: 0 };

  if (hasPayout) {
    const safeCap = isReit ? 90 : 75;
    if (payoutRaw <= 60) breakdown.safety = 30;
    else if (payoutRaw <= safeCap) breakdown.safety = 15;
    else breakdown.safety = -10;
  } else {
    breakdown.safety = 12;
  }
  if (trapSignature) breakdown.safety = 0;

  if (hasYield) {
    if (yieldPct >= 3 && yieldPct <= 7) breakdown.yield = 25;
    else if ((yieldPct >= 1.5 && yieldPct < 3) || (yieldPct > 7 && yieldPct <= 10)) breakdown.yield = 15;
    else if (yieldPct > 10 && yieldPct <= 12) breakdown.yield = 8;
    else if (yieldPct > 12) breakdown.yield = 0;
    else breakdown.yield = 5;
  }

  if (hasGrowth) {
    if (growthPct >= 8) breakdown.growth = 20;
    else if (growthPct >= 4) breakdown.growth = 10;
    else if (growthPct > 0) breakdown.growth = 5;
    else breakdown.growth = 0;
  }
  if (trapSignature) breakdown.growth = 0;

  if (tier === 'king') breakdown.longevity = 25;
  else if (tier === 'aristocrat') breakdown.longevity = 20;
  else if (tier === 'achiever') breakdown.longevity = 10;
  else if (streakYears >= 50) breakdown.longevity = 25;
  else if (streakYears >= 25) breakdown.longevity = 20;
  else if (streakYears >= 10) breakdown.longevity = 10;
  else if (streakYears >= 5) breakdown.longevity = 5;

  let penalty = 0;
  const penaltyReasons = [];
  if (isLeveraged) { penalty += 15; penaltyReasons.push({ key: 'leveraged', label: 'Leveraged ETF', delta: -15 }); }
  if (isCef)       { penalty += 5;  penaltyReasons.push({ key: 'cef',       label: 'CEF structure', delta: -5  }); }

  let fundamentalBonus = 0;
  const fundamentalReasons = [];
  const num = (v) => {
    const x = Number(v);
    return Number.isFinite(x) ? x : null;
  };

  const revStab = num(stock.quality_revenue_stability);
  if (revStab != null && revStab <= 0.05) {
    fundamentalBonus += 5;
    fundamentalReasons.push({ key: 'rev_stability', label: 'Stable revenue', value: revStab, delta: 5 });
  }

  const roe = num(stock.quality_return_on_equity);
  if (roe != null && roe >= 0.15) {
    fundamentalBonus += 5;
    fundamentalReasons.push({ key: 'roe', label: 'Strong ROE (≥15%)', value: roe, delta: 5 });
  }

  const de = num(stock.quality_debt_to_equity);
  if (de != null && de > 2.0) {
    penalty += 5;
    penaltyReasons.push({ key: 'high_de', label: 'High Debt/Equity (>2.0)', value: de, delta: -5 });
  }

  const delta = num(stock.dividend_policy_delta);
  const trailing = num(stock.trailing_annual_dividend_rate_usd);
  if (delta != null && trailing != null && trailing > 0) {
    const ratio = delta / trailing;
    if (ratio >= 0.05) {
      fundamentalBonus += 3;
      fundamentalReasons.push({ key: 'policy_hike', label: 'Recent dividend hike', value: ratio, delta: 3 });
    } else if (ratio <= -0.10) {
      penalty += 5;
      penaltyReasons.push({ key: 'policy_cut', label: 'Recent dividend cut', value: ratio, delta: -5 });
    }
  }

  // Sep 2026 — BDC NAV Growth signal (Phase 2). Populated weekly by
  // the `bdc_nav_growth_backfill` cron. A positive 5-quarter NAV/share
  // trend means the loan book is expanding — a real BDC-specific
  // healthiness signal that GAAP EPS + payout ratio can't capture.
  // Kept modest (±3 pts) so it can't dominate the score by itself.
  const bdcNavGrowth = num(stock.bdc_nav_growth_5q_pct);
  if (!!stock.is_bdc && bdcNavGrowth != null) {
    if (bdcNavGrowth >= 3) {
      fundamentalBonus += 3;
      fundamentalReasons.push({ key: 'bdc_nav_growth', label: 'BDC loan book expanding (NAV +≥3% over 5Q)', value: bdcNavGrowth, delta: 3 });
    } else if (bdcNavGrowth >= 0) {
      fundamentalBonus += 1;
      fundamentalReasons.push({ key: 'bdc_nav_stable', label: 'BDC loan book stable (NAV flat over 5Q)', value: bdcNavGrowth, delta: 1 });
    } else if (bdcNavGrowth <= -5) {
      penalty += 3;
      penaltyReasons.push({ key: 'bdc_nav_shrinking', label: 'BDC loan book shrinking (NAV −≥5% over 5Q)', value: bdcNavGrowth, delta: -3 });
    } else {
      // −5% < growth < 0 — mild shrink, small caution
      penalty += 1;
      penaltyReasons.push({ key: 'bdc_nav_mild_shrink', label: 'BDC loan book slightly shrinking', value: bdcNavGrowth, delta: -1 });
    }
  }

  breakdown.fundamentals = fundamentalBonus;

  // June 2026 — 60/40 composition. MUST mirror backend exactly.
  const raw = breakdown.safety + breakdown.yield + breakdown.growth + breakdown.longevity + fundamentalBonus - penalty;
  const trailingCore = Math.max(0, Math.min(100, raw)) * 0.6;
  const partial = !forward;
  const score = forward
    ? Math.max(0, Math.min(100, Math.round(trailingCore + forward.total)))
    : Math.round(trailingCore);

  return {
    score,
    breakdown,
    penalty,
    penaltyReasons,
    fundamentalReasons,
    isReit,
    isLeveraged,
    isCef,
    forward,
    trailingCore: Math.round(trailingCore),
    partial,
  };
}

/**
 * Map score (0–100) to display label + colour band.
 */
export function qualityBand(score) {
  if (score == null) return { label: '—', short: '—', tone: 'muted' };
  if (score >= 85) return { label: 'Elite',     short: 'A+', tone: 'emerald' };
  if (score >= 70) return { label: 'Strong',    short: 'A',  tone: 'green' };
  if (score >= 55) return { label: 'Healthy',   short: 'B',  tone: 'lime' };
  if (score >= 40) return { label: 'Average',   short: 'C',  tone: 'amber' };
  if (score >= 25) return { label: 'Weak',      short: 'D',  tone: 'orange' };
  return                   { label: 'Risky',    short: 'F',  tone: 'rose' };
}

/**
 * Tailwind classes for the badge — kept here so every surface renders
 * an identical pill.
 */
export function qualityColorClasses(tone) {
  const map = {
    emerald: { bg: 'bg-emerald-100 dark:bg-emerald-950/60', text: 'text-emerald-700 dark:text-emerald-300', ring: 'ring-emerald-400/40', dot: 'bg-emerald-500' },
    green:   { bg: 'bg-green-100   dark:bg-green-950/60',   text: 'text-green-700   dark:text-green-300',   ring: 'ring-green-400/40',   dot: 'bg-green-500'   },
    lime:    { bg: 'bg-lime-100    dark:bg-lime-950/60',    text: 'text-lime-700    dark:text-lime-300',    ring: 'ring-lime-400/40',    dot: 'bg-lime-500'    },
    amber:   { bg: 'bg-amber-100   dark:bg-amber-950/60',   text: 'text-amber-700   dark:text-amber-300',   ring: 'ring-amber-400/40',   dot: 'bg-amber-500'   },
    orange:  { bg: 'bg-orange-100  dark:bg-orange-950/60',  text: 'text-orange-700  dark:text-orange-300',  ring: 'ring-orange-400/40',  dot: 'bg-orange-500'  },
    rose:    { bg: 'bg-rose-100    dark:bg-rose-950/60',    text: 'text-rose-700    dark:text-rose-300',    ring: 'ring-rose-400/40',    dot: 'bg-rose-500'    },
    muted:   { bg: 'bg-muted/40',                            text: 'text-muted-foreground',                  ring: 'ring-border/40',       dot: 'bg-muted-foreground/40' },
  };
  return map[tone] || map.muted;
}

/** Human label for the coverage metric denominator. */
export function coverageMetricLabel(metric) {
  const m = (metric || '').toUpperCase();
  if (m.startsWith('NII')) return 'Net Investment Income coverage';
  if (m === 'FFO') return 'FFO coverage';
  if (m === 'DCF') return 'Distributable Cash Flow coverage';
  return 'Free Cash Flow coverage';
}

/**
 * Build the "Why this score?" tooltip body. Returns null when
 * there's no quality data so the caller can render a fallback.
 */
export function qualityTooltip(quality) {
  if (!quality) return 'Not enough data on file to score this stock fairly. Live-refresh the ticker to populate it.';
  const { breakdown, penalty, penaltyReasons, fundamentalReasons, isFund, fundCategory, forward, partial, trailingCore } = quality;
  const lines = [];
  if (isFund) {
    lines.push(`Yield stability: ${breakdown.yield} / 30 pts`);
    lines.push(`Distribution growth: ${breakdown.growth} / 25 pts`);
    lines.push(`Longevity: ${breakdown.longevity} / 25 pts`);
    lines.push(`Asset confidence: ${breakdown.confidence} / 20 pts`);
    if (penalty) {
      const items = (penaltyReasons || []).map(r => r.label).join(', ');
      lines.push(`Penalty: -${penalty} pts${items ? ` (${items})` : ''}`);
    }
    if (forward) {
      lines.push('');
      lines.push(`${coverageMetricLabel(forward.metric)}: ${forward.ratio.toFixed(2)}× (TTM) → +${Math.round(forward.total)} / 40 forward pts`);
    }
    lines.push('');
    if (fundCategory) lines.push(`Fund category: ${fundCategory}`);
    lines.push('Funds are graded by yield stability + asset class — corporate metrics (ROIC, FCF, payout ratio) don\'t apply.');
    lines.push('Score capped at 90 — full "A+ Elite" reserved for stocks where we have the complete corporate picture.');
    return lines.join('\n');
  }
  // Corporate-stock breakdown — Trailing Core + Forward Signals
  lines.push(`Trailing core: ${trailingCore ?? '—'} / 60 pts`);
  lines.push(`  • Safety: ${breakdown.safety} / 30`);
  lines.push(`  • Yield: ${breakdown.yield} / 25`);
  lines.push(`  • Growth: ${breakdown.growth} / 20`);
  lines.push(`  • Longevity: ${breakdown.longevity} / 25`);
  if (breakdown.fundamentals) {
    const items = (fundamentalReasons || []).map(r => r.label).join(', ');
    lines.push(`  • Fundamentals: +${breakdown.fundamentals}${items ? ` (${items})` : ''}`);
  }
  if (penalty) {
    const items = (penaltyReasons || []).map(r => r.label).join(', ');
    lines.push(`  • Penalty: -${penalty}${items ? ` (${items})` : ''}`);
  }
  lines.push('');
  if (forward) {
    lines.push(`Forward signals: +${Math.round(forward.total)} / 40 pts`);
    lines.push(`  • ${coverageMetricLabel(forward.metric)}: ${forward.ratio.toFixed(2)}× (TTM, SEC filings)`);
    if (forward.trendPct != null) {
      lines.push(`  • Coverage trend: ${forward.trendPct > 0 ? '+' : ''}${forward.trendPct.toFixed(1)}% over 4 quarters`);
    }
  } else if (partial) {
    lines.push('Partial score — forward coverage data (SEC filings) not on file yet, so this reflects trailing pillars only (max 60). It upgrades automatically once coverage data lands.');
  }
  lines.push('');
  lines.push('Click the pill for the full breakdown. Read alongside the Income Risk badge for the full picture.');
  return lines.join('\n');
}
